import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Divider, Drawer, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArchiveIcon,
  ArrowUpRightIcon,
  BriefcaseIcon,
  PencilIcon,
  PresentationIcon,
  RotateCcwIcon,
  Trash2Icon,
  UserPlusIcon,
  WalletIcon,
  XIcon,
} from 'lucide-react'
import { Skeleton } from '@repo/ui'
import {
  clientTierOptionLabel,
  foundersListStatusLabel,
  foundersTermDaysLeft,
  FOUNDERS_LIST_STATUS_META,
  FOUNDERS_TERM_SOON_DAYS,
  hasFoundersListTerms,
  isWonWithoutPaying,
  type Accuracy,
  type ForecastFigure,
  type FoundersListTerms,
  type SalesStage,
} from '@repo/types'

import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { OutreachCertificatePanel } from 'components/feedback/OutreachCertificate'
import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { commercePlatformDisplay } from 'constants/commercePlatforms'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { useSalesAccount } from 'hooks/api/useGtmSalesPipeline'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'
import type { SalesDealCard, SalesDealEvent, SalesLeadRow } from 'services'
import { vhMinus } from 'utils/textZoom'

import { shortDate, ToneChip } from '../InternalAnalyticsPage/components/shared'
import { DealMarks, PayingNowChip } from './AutoWonMarks'
import { atLeast, CREATE_DEMO_TIP, daysLabel, ENTERPRISE_FLOOR_TIP, FOUNDERS_LIST_TERM, FOUNDERS_LIST_TONE, MANAGE_SALES_TIP, money, OPEN_DEAL_WARNING, planLabel, sourceGroupLabel, STAGE_LABEL, valueLabel } from './dealsModel'
import { figureBasisLabel, ratioLabel } from './ForecastFigures'
import { ActivationShareFigure, AnnualValueFigure, PredictedArrFigure, TagMarginFigure } from './DealValueFigures'

const DRAWER_WIDTH = 520

const Row = ({ label, children, term }: { label: string; children: React.ReactNode; term?: string }) => (
  <Stack direction="row" spacing={3} alignItems="baseline" sx={{ minWidth: 0 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 148, flexShrink: 0 }}>
      <Typography variant="body3" color="text.secondary">
        {label}
      </Typography>
      {term && <InfoTooltip term={term} />}
    </Stack>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Stack>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack spacing={3}>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {title}
    </Typography>
    {children}
  </Stack>
)

/**
 * VG-27 — the Founder's List terms on the open deal. Rendered only when the deal carries any: a
 * section of four em-dashes on every ordinary deal would be noise in a drawer that is already long.
 *
 * The term-end row is the one that earns its place — it names the handover (the base fee resuming,
 * the plan moving off Business) while there is still time to have the conversation.
 */
const FoundersTermsSection = ({ terms }: { terms: FoundersListTerms }) => {
  const daysLeft = foundersTermDaysLeft(terms.term_ends_on)
  return (
    <Section title="Founder&apos;s List terms">
      <Row label="Base fee waived" term="founders-list-waived-base">
        <Typography variant="body3">
          {terms.waived_base_months === null
            ? 'Not set'
            : `${terms.waived_base_months} ${terms.waived_base_months === 1 ? 'month' : 'months'}`}
        </Typography>
      </Row>
      <Row label="Activation bank" term="founders-list-activation-bank">
        <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="body3">
            {terms.activation_bank === null ? 'Not set' : `${terms.activation_bank.toLocaleString('en-US')} activations`}
          </Typography>
          {terms.activation_bank !== null &&
            (terms.bank_released_at ? (
              <ToneChip tone="positive" label={`Released ${shortDate(terms.bank_released_at)}`} />
            ) : (
              <ToneChip tone="neutral" label="Not released" />
            ))}
        </Stack>
      </Row>
      <Row label="Term ends">
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="body3">{terms.term_ends_on ? shortDate(`${terms.term_ends_on}T12:00:00`) : 'Not set'}</Typography>
          {daysLeft !== null && daysLeft <= FOUNDERS_TERM_SOON_DAYS && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {daysLeft < 0
                ? 'The base fee has resumed. Check the plan still fits their volume.'
                : 'The base fee resumes then, and the plan moves to whatever fits their volume.'}
            </Typography>
          )}
        </Stack>
      </Row>
    </Section>
  )
}

/** "A" + "B" → "A. B", without doubling a full stop the first already has. */
const joinSentences = (a: string | null | undefined, b: string | null | undefined): string =>
  [a, b].filter(Boolean).reduce<string>((acc, x) => (acc ? `${acc}${/[.!?]$/.test(acc) ? '' : '.'} ${x}` : (x as string)), '')

/** One figure in the drawer: the amount (with MRR), its basis, then provenance or the reason, and as-of. */
const FigureRow = ({
  label,
  term,
  figure,
  mrr,
  extra,
  snapshot,
}: {
  label: string
  term: string
  figure: ForecastFigure
  mrr?: number | null
  extra?: string | null
  snapshot?: boolean
}) => (
  <Row label={label} term={term}>
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="body3" sx={{ fontWeight: figure.amount === null ? 400 : 600 }} color={figure.amount === null ? 'text.secondary' : 'text.primary'}>
          {figure.amount === null ? '—' : `${money(figure.amount, figure.currency)} / yr`}
          {figure.amount !== null && mrr !== undefined && mrr !== null ? ` · ${money(mrr, figure.currency)} / mo` : ''}
        </Typography>
        {figure.amount !== null && figureBasisLabel(figure.basis) && <ToneChip tone="neutral" label={figureBasisLabel(figure.basis)!} />}
        {snapshot && <ToneChip tone="info" label="Snapshot" glossaryId="sales-prediction-snapshot" />}
      </Stack>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {joinSentences(figure.amount === null ? figure.reason : figure.detail, extra)}
      </Typography>
      {figure.as_of && (
        <Typography variant="body3" color="text.secondary">
          {snapshot ? 'Snapshot taken' : 'As of'} {shortDate(figure.as_of)}
        </Typography>
      )}
    </Stack>
  </Row>
)

const AccuracyRow = ({ label, term, accuracy }: { label: string; term: string; accuracy: Accuracy }) => (
  <Row label={label} term={term}>
    <Stack spacing={1}>
      <Typography variant="body3" sx={{ fontWeight: accuracy.ratio === null ? 400 : 600 }} color={accuracy.ratio === null ? 'text.secondary' : 'text.primary'}>
        {ratioLabel(accuracy.ratio)}
      </Typography>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {accuracy.ratio === null
          ? accuracy.reason
          : Math.abs(accuracy.ratio - 1) < 0.005
            ? 'Actual ÷ predicted: a 1:1 forecast.'
            : `Actual ÷ predicted. The inputs ${accuracy.ratio > 1 ? 'under-promised' : 'over-promised'}.`}
      </Typography>
    </Stack>
  </Row>
)

const PLAN_SOURCE_COPY: Record<string, string> = {
  stripe: 'from Stripe',
  deal: 'set on the deal',
  form: "the waitlist form's plan of interest",
  tier: "the tier's default plan",
}

/**
 * VRG-701 — the deal's POTENTIAL revenue (Spencer, 2026-10-14): the subscription for its plan and
 * the activations its annual products would bill, priced on the Stripe plan catalog by the same
 * model as the Signups grid (`dealPotentialRevenue` → `potentialRevenue`). The inputs come from
 * the company's ROI calculator unless they were edited on the deal, and say which.
 */
const PotentialSection = ({ deal }: { deal: SalesDealCard }) => {
  const p = deal.potential
  if (!p) return null
  const i = p.inputs
  return (
    <Section title="Potential revenue">
      <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }} data-testid="potential-inputs">
        <Typography variant="body3" color="text.secondary">
          Inputs
        </Typography>
        {i.source && <ToneChip tone={i.source === 'edited' ? 'info' : 'neutral'} label={i.source === 'edited' ? 'Edited' : 'From calculator'} glossaryId="deal-calc-inputs" />}
      </Stack>
      <Row label="Products a year" term="deal-calc-inputs">
        <Typography variant="body3">{i.units != null ? i.units.toLocaleString('en-US') : 'Not known: no calculator figure yet'}</Typography>
      </Row>
      <Row label="Share activated" term="activation-share">
        <ActivationShareFigure deal={deal} />
      </Row>
      {(i.price != null || i.cac != null) && (
        <Row label="Price · CAC">
          <Typography variant="body3">
            {i.price != null ? money(i.price) : '—'} · {i.cac != null ? money(i.cac) : '—'}
          </Typography>
        </Row>
      )}
      <Row label="Subscription" term="potential-subscription-revenue">
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body3" sx={{ fontWeight: p.subscription_annual != null ? 600 : 400 }} color={p.subscription_annual != null ? 'text.primary' : 'text.secondary'} data-testid="potential-subscription">
              {p.subscription_annual != null ? atLeast(p.plan_substituted, `${money(p.subscription_annual)} / yr`) : 'No plan to price'}
            </Typography>
            {p.plan_substituted && p.subscription_annual != null && <InfoTooltip text={ENTERPRISE_FLOOR_TIP} />}
          </Stack>
          {p.plan && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {planLabel(p.plan)} plan, {PLAN_SOURCE_COPY[p.plan_source ?? 'deal']}
              {p.plan_substituted ? '; Enterprise is custom-priced, so it is priced as Scale (a floor)' : ''}.
            </Typography>
          )}
        </Stack>
      </Row>
      <Row label="Activations" term="potential-activation-revenue">
        <Typography variant="body3" sx={{ fontWeight: p.activation_annual != null ? 600 : 400 }} color={p.activation_annual != null ? 'text.primary' : 'text.secondary'}>
          {p.activation_annual != null ? `${money(p.activation_annual)} / yr` : 'Needs products a year'}
        </Typography>
      </Row>
      <Row label="Total (ARR)">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body3" sx={{ fontWeight: 600 }} data-testid="potential-total">
            {p.total != null ? atLeast(p.plan_substituted && p.subscription_annual != null, `${money(p.total)} / yr · ${money(p.total / 12)} / mo`) : '—'}
          </Typography>
          {p.plan_substituted && p.subscription_annual != null && p.total != null && <InfoTooltip text={ENTERPRISE_FLOOR_TIP} />}
        </Stack>
      </Row>
      <Divider />
      {/* VRG-736 — the deal's three figures: ARR (a manual figure wins), tags beside it, and both. */}
      <Row label="Predicted ARR" term="deal-anticipated-value">
        <PredictedArrFigure deal={deal} />
      </Row>
      <Row label="Tag margin" term="tag-margin">
        <TagMarginFigure deal={deal} />
      </Row>
      <Row label="Annual value" term="predicted-annual-value">
        <AnnualValueFigure deal={deal} />
      </Row>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        Priced on the plan catalog synced from Stripe: the plan&apos;s monthly price × 12, and the graduated per-activation price on the
        activations expected (products × share activated) ÷ 12 a month. Tag margin is hardware, so it sits beside ARR, never in it. Edit
        the inputs and the share on Edit deal.
      </Typography>
    </Section>
  )
}

/** VRG-692 — the four fields, predicted and actual, for Vera and for the brand, with accuracy. */
const ForecastSection = ({ deal }: { deal: SalesDealCard }) => {
  const f = deal.forecast
  const split =
    f.predicted_arr.plan_annual !== null && f.predicted_arr.activation_annual !== null
      ? `Plan ${money(f.predicted_arr.plan_annual)} + activations ${money(f.predicted_arr.activation_annual)} a year; a subscription commits only the plan.`
      : null
  const snap = f.snapshot
  return (
    <Section title="Predicted vs actual">
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {snap
          ? `The predictions were frozen ${snap.trigger === 'backfill' ? 'after the outcome (this company was a client before snapshots existed)' : `when Vera saw ${snap.trigger === 'paid_client' ? 'its first paid invoice' : 'its subscription'}`}, ${shortDate(snap.taken_at)}, so accuracy compares against what was predicted then.`
          : 'Live predictions from today’s inputs. They are frozen the first time Vera sees a paid invoice or an active subscription.'}
      </Typography>
      <Typography variant="body3" sx={{ fontWeight: 500 }}>
        Vera revenue
      </Typography>
      <FigureRow label="Predicted ARR" term="deal-anticipated-value" figure={f.predicted_arr} mrr={f.predicted_arr.mrr} extra={split} snapshot={f.predicted_arr.source === 'snapshot'} />
      <FigureRow label="Committed ARR" term="sales-committed-arr" figure={f.actual_arr} mrr={f.actual_arr.mrr} />
      <AccuracyRow label="Revenue accuracy" term="sales-forecast-accuracy" accuracy={f.revenue_accuracy} />
      <Typography variant="body3" sx={{ fontWeight: 500, pt: 2 }}>
        The brand’s return
      </Typography>
      <FigureRow label="Predicted ROI" term="brand-predicted-roi" figure={f.predicted_roi} snapshot={f.predicted_roi.source === 'snapshot'} />
      <FigureRow
        label="Actual ROI"
        term="brand-actual-roi"
        figure={f.actual_roi}
        extra={f.actual_roi.multiple !== null && f.actual_roi.amount !== null ? `The Analytics multiple over the same window is ${f.actual_roi.multiple.toFixed(1)}×.` : null}
      />
      <AccuracyRow label="ROI accuracy" term="brand-roi-accuracy" accuracy={f.roi_accuracy} />
    </Section>
  )
}

const eventLine = (e: SalesDealEvent): string => {
  if (e.kind === 'opened') return 'Entered the pipeline as a Lead'
  // VRG-700 — an automatic Won says so in Spencer's words (the evidence line), and an override
  // says what it stops.
  if (e.kind === 'stage' && e.set_by === 'auto' && e.to_stage === 'paid_client' && e.subscription_id && e.evidence) return e.evidence
  if (e.kind === 'override') return 'Automatic Won overridden by hand'
  if (e.kind === 'closed') return `Closed: ${e.evidence ?? ''}`.trim()
  if (e.kind === 'reopened') return `Reopened at ${e.to_stage ? STAGE_LABEL[e.to_stage] : 'its stage'}`
  if (e.kind === 'evidence') return `${e.to_stage ? STAGE_LABEL[e.to_stage] : 'Stage'} confirmed by evidence`
  const from = e.from_stage ? STAGE_LABEL[e.from_stage] : '—'
  const to = e.to_stage ? STAGE_LABEL[e.to_stage] : '—'
  return `${from} → ${to}`
}

/**
 * One sales company, opened from a board card or a list row: its deal (stage, value and where
 * the value came from), lead source, company facts and links, leads, and the append-only stage
 * history. Every write goes through the shared forms the parent owns (§28/§30).
 */
export const SalesCompanyDrawer = ({
  accountId,
  dealId,
  onSelectDeal,
  onAddDeal,
  canManage,
  canCreateDemo,
  onCreateDemo,
  onClose,
  onEditCompany,
  onEditDeal,
  onAddLead,
  onEditLead,
  onRemoveLead,
  onCloseDeal,
  onReopenDeal,
}: {
  accountId: string | null
  /** VRG-701 — which of the company's deals the panel is on (null = its primary deal). */
  dealId: string | null
  onSelectDeal: (dealId: string) => void
  onAddDeal: () => void
  canManage: boolean
  /** VRG-723 — Create demo, pre-picked on this company (gated on the Demos permission). */
  canCreateDemo: boolean
  onCreateDemo: () => void
  onClose: () => void
  onEditCompany: () => void
  onEditDeal: (deal: SalesDealCard) => void
  onAddLead: () => void
  onEditLead: (lead: SalesLeadRow) => void
  onRemoveLead: (lead: SalesLeadRow) => void
  onCloseDeal: (deal: SalesDealCard) => void
  onReopenDeal: (deal: SalesDealCard) => void
}) => {
  const theme = useTheme()
  const referrer = useReferrerState('Sales')
  const navigate = useNavigate()
  const { data, isLoading, error } = useSalesAccount(accountId)
  // VRG-701 — several deals per company: the one picked, else the primary deal.
  const deal = (dealId ? data?.deals.find((d) => d.id === dealId) : null) ?? data?.deal ?? null
  const open = deal?.status === 'open'
  const deals = data?.deals ?? []
  const openNotWon = deals.filter((d) => d.status === 'open' && d.stage !== 'paid_client').length

  const menu: ActionDropdownItem[] = [
    { label: 'Edit company', icon: <PencilIcon strokeWidth={1} size={16} />, onClick: onEditCompany, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP },
    ...(deal
      ? [
          { label: 'Edit deal', icon: <WalletIcon strokeWidth={1} size={16} />, onClick: () => onEditDeal(deal), disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, visible: open },
        ]
      : []),
    { label: 'Add deal', icon: <BriefcaseIcon strokeWidth={1} size={16} />, onClick: onAddDeal, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP },
    { label: 'Add person', icon: <UserPlusIcon strokeWidth={1} size={16} />, onClick: onAddLead, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP },
    { label: 'Create demo', icon: <PresentationIcon strokeWidth={1} size={16} />, onClick: onCreateDemo, disabled: !canCreateDemo, tooltip: canCreateDemo ? undefined : CREATE_DEMO_TIP },
    ...(deal
      ? [
          // §73 — the end-of-life action is last; reopen takes its slot when the deal is closed.
          { label: 'Reopen deal', icon: <RotateCcwIcon strokeWidth={1} size={16} />, onClick: () => onReopenDeal(deal), disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, visible: !open, divider: true },
          { label: 'Close deal', icon: <ArchiveIcon strokeWidth={1} size={16} />, onClick: () => onCloseDeal(deal), disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, visible: open, divider: true },
        ]
      : []),
  ]

  return (
    <Drawer
      anchor="right"
      open={!!accountId}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          margin: 4,
          borderRadius: 3,
          maxHeight: vhMinus(32),
          minWidth: DRAWER_WIDTH,
          maxWidth: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
          <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
            {data?.name ?? (isLoading ? '' : 'Company')}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
            {data && <ActionDropdown items={menu} />}
            <IconButton onClick={onClose} aria-label="Close" sx={{ color: theme.palette.text.primary }}>
              <XIcon strokeWidth={1} />
            </IconButton>
          </Stack>
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          A brand on the sales pipeline. It links to its platform company once one exists.
        </Typography>
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 6 }}>
        {error && !data ? (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Could not load this company. Close the panel and try again.
          </Typography>
        ) : isLoading || !data ? (
          <Stack spacing={4}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={18} width={i % 2 ? '70%' : '90%'} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={6} divider={<Divider />}>
            {deals.length > 1 && (
              <Section title={`Deals (${deals.length})`}>
                {openNotWon > 1 && (
                  <Typography variant="body3" color="warning.main" sx={{ lineHeight: 1.5 }}>
                    {openNotWon} open deals that are not Won. {OPEN_DEAL_WARNING.split('. ').slice(1).join('. ')}
                  </Typography>
                )}
                <Stack spacing={2} data-testid="drawer-deals">
                  {deals.map((d) => {
                    const selected = d.id === deal?.id
                    return (
                      <Box
                        key={d.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        onClick={() => onSelectDeal(d.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onSelectDeal(d.id)
                        }}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          cursor: 'pointer',
                          border: `1px solid ${selected ? theme.palette.brand[500] : theme.palette.primary[100]}`,
                          // §34 selectable card: tint = considering, border = chosen.
                          backgroundColor: selected ? alpha(theme.palette.brand[500], 0.1) : 'transparent',
                          '&:hover': { backgroundColor: alpha(theme.palette.brand[500], selected ? 0.1 : 0.06) },
                        }}
                      >
                        <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                          <Typography variant="body3" sx={{ fontWeight: 500 }}>
                            {d.name ?? (d.is_first_deal ? 'First deal' : `Deal opened ${shortDate(d.opened_at)}`)}
                          </Typography>
                          <ToneChip
                            tone={d.status === 'closed' ? 'negative' : d.stage === 'paid_client' ? 'positive' : 'neutral'}
                            label={d.status === 'closed' ? `Closed (${d.closed_reason})` : STAGE_LABEL[d.stage]}
                          />
                          {d.source.group === 'expansion' && <ToneChip tone="info" label="Expansion" glossaryId="sales-expansion" />}
                          <Typography variant="body3" color="text.secondary">
                            {valueLabel(d.value)}
                            {d.plan ? ` · ${planLabel(d.plan)}` : ''}
                          </Typography>
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              </Section>
            )}
            {deal && (
              <Section title={deals.length > 1 ? `Deal: ${deal.name ?? (deal.is_first_deal ? 'first deal' : `opened ${shortDate(deal.opened_at)}`)}` : 'Deal'}>
                <Row label="Stage" term="sales-deal">
                  <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Typography variant="body3">
                      {deal.status === 'closed' ? `Closed (${deal.closed_reason}) at ${STAGE_LABEL[deal.stage]}` : STAGE_LABEL[deal.stage]}
                    </Typography>
                    {deal.status === 'closed' && deal.closed_reason === 'lost' && <ToneChip tone="negative" label="Lost" glossaryId="sales-deal-lost" />}
                    {deal.stage_set_by === 'manual' && <ToneChip tone="caution" label="Set by hand" />}
                    <DealMarks wonWithoutPaying={isWonWithoutPaying(deal)} autoWon={deal.auto_won} />
                    {/* VRG-727 — the company's money status: Paying now, or Not paying once it lapsed. */}
                    <PayingNowChip status={deal.paying_now} />
                  </Stack>
                </Row>
                <Row label="Days in stage">
                  <Typography variant="body3">{daysLabel(deal.days_in_stage)}</Typography>
                </Row>
                <Row label="Owner">
                  <Typography variant="body3">{deal.owner?.name ?? 'Nobody'}</Typography>
                </Row>
                <Row label="Expected close">
                  <Typography variant="body3">{deal.expected_close_on ? shortDate(`${deal.expected_close_on}T12:00:00`) : 'Not set'}</Typography>
                </Row>
                <Row label="Next step">
                  <Typography variant="body3">{deal.next_step ?? 'Not set'}</Typography>
                </Row>
                <Row label="Plan" term="sales-deal-plan">
                  <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Typography variant="body3">{planLabel(deal.plan)}</Typography>
                    {deal.plan_source === 'stripe' && <ToneChip tone="positive" label="From Stripe" />}
                  </Stack>
                </Row>
                <Row label="Deal source" term={deal.source.group === 'expansion' ? 'sales-expansion' : 'lead-source'}>
                  <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <ToneChip tone={deal.source.group === 'expansion' ? 'info' : 'neutral'} label={sourceGroupLabel(deal.source.group)} />
                    <Typography variant="body3" color="text.secondary">
                      {deal.deal_source.value === null
                        ? "the company's first touch"
                        : deal.deal_source.set_by === 'auto'
                          ? 'set by Vera: the company was already a client'
                          : 'set by hand'}
                    </Typography>
                  </Stack>
                </Row>
                <Row label="People">
                  <Typography variant="body3" color={deal.people.length ? 'text.primary' : 'text.secondary'} sx={{ lineHeight: 1.5 }}>
                    {deal.people.length ? deal.people.map((p) => p.name ?? p.email).join(', ') : 'Nobody yet'}
                    {deal.people.length && !deal.people_explicit ? ' (the company’s people)' : ''}
                  </Typography>
                </Row>
              </Section>
            )}

            {deal && hasFoundersListTerms(deal.founders_terms) && <FoundersTermsSection terms={deal.founders_terms} />}
            {deal && <PotentialSection deal={deal} />}
            {deal && <ForecastSection deal={deal} />}

            <Section title="Company lead source (first touch)">
              <Row label="Source" term="lead-source">
                <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <ToneChip tone="neutral" label={sourceGroupLabel(data.source.group)} />
                  <Typography variant="body3" color="text.secondary">
                    {data.source.set_by === 'manual' ? 'set by hand' : data.source.set_by === 'auto' ? 'filled in by Vera' : 'not recorded'}
                  </Typography>
                </Stack>
              </Row>
              <Row label="Channel" term="source-channel">
                <Typography variant="body3">{sourceChannelLabel(data.source.channel)}</Typography>
              </Row>
              <Row label="Sub-source">
                <Typography variant="body3">{data.source.detail ?? '—'}</Typography>
              </Row>
            </Section>

            <Section title="Company">
              <Row label="Website">
                <Typography variant="body3">{data.domain ?? '—'}</Typography>
              </Row>
              <Row label="Industry">
                <Typography variant="body3">{data.industry ?? '—'}</Typography>
              </Row>
              <Row label="Tier" term="client-tier">
                <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
                  {data.tier ? clientTierOptionLabel(data.tier) : data.suggested_tier ? `Not set (the calculator suggests ${clientTierOptionLabel(data.suggested_tier.tier).split(' · ')[0]})` : '—'}
                </Typography>
              </Row>
              {/* VG-27 — read-only here; Edit company changes it. Always shown, including "Not on the
                  list": a blank would read as missing data, and the absence IS the answer. */}
              <Row label="Founder&apos;s List" term={data.founders_list_status ? FOUNDERS_LIST_TERM[data.founders_list_status] : 'founders-list'}>
                {data.founders_list_status ? (
                  <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <ToneChip tone={FOUNDERS_LIST_TONE[data.founders_list_status]} label={foundersListStatusLabel(data.founders_list_status)} />
                    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {FOUNDERS_LIST_STATUS_META[data.founders_list_status].note}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body3" color="text.secondary">
                    Not on the list
                  </Typography>
                )}
              </Row>
              <Row label="Commerce platform" term="commerce-platform">
                {/* VRG-716 — read-only here; Edit company changes it (and makes it manual). */}
                {data.commerce_platform ? (
                  <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <ToneChip tone="neutral" label={commercePlatformDisplay(data.commerce_platform)} />
                    <Typography variant="body3" color="text.secondary">
                      {data.commerce_platform_source === 'manual' ? 'set by hand' : 'detected from the site'}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body3" color="text.secondary">
                    Not set. Making a demo detects it.
                  </Typography>
                )}
              </Row>
              <Row label="Location">
                <Typography variant="body3">{[data.city, data.country].filter(Boolean).join(', ') || '—'}</Typography>
              </Row>
              <Row label="Platform company">
                {data.company ? (
                  <Link component={RouterLink} to={ROUTES.INTERNAL_COMPANY(data.company.id)} state={referrer} variant="body3" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {data.company.name}
                    <ArrowUpRightIcon size={13} strokeWidth={1} />
                  </Link>
                ) : (
                  <Typography variant="body3" color="text.secondary">
                    None yet. Links itself when a platform company with this website is created.
                  </Typography>
                )}
              </Row>
              <Row label="Demo">
                {data.demo_company ? (
                  <Link component={RouterLink} to={ROUTES.INTERNAL_COMPANY(data.demo_company.id)} state={referrer} variant="body3" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {data.demo_company.name}
                    <ArrowUpRightIcon size={13} strokeWidth={1} />
                  </Link>
                ) : (
                  <Typography variant="body3" color="text.secondary">
                    None
                  </Typography>
                )}
              </Row>
              {data.notes && (
                <Row label="Notes">
                  <Typography variant="body3" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {data.notes}
                  </Typography>
                </Row>
              )}
            </Section>

            {/* VRG-723 — the demo's outreach certificate, from the same panel as the Demos page.
                Its endpoints sit behind the Demos permission, so without it the section is absent
                rather than an error (a surface the user cannot reach, §27). */}
            {data.demo_company && canCreateDemo && (
              <Section title="Outreach certificate">
                <OutreachCertificatePanel companyId={data.demo_company.id} />
              </Section>
            )}

            <Section title={`People (${data.leads.length})`}>
              {data.leads.length === 0 ? (
                <Typography variant="body3" color="text.secondary">
                  No leads yet.
                </Typography>
              ) : (
                data.leads.map((l) => (
                  <Stack key={l.id} direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body3" noWrap>
                        {l.name ?? l.email}
                        {l.title ? ` · ${l.title}` : ''}
                      </Typography>
                      <Typography variant="body3" color="text.secondary" noWrap>
                        {l.email}
                        {l.signup ? ` · joined the waitlist ${shortDate(l.signup.created_at)}${l.signup.granted_at ? ', access granted' : ''}` : ''}
                      </Typography>
                    </Stack>
                    <ActionDropdown
                      items={[
                        { label: 'Edit person', icon: <PencilIcon strokeWidth={1} size={16} />, onClick: () => onEditLead(l), disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP },
                        {
                          label: 'Open in Access',
                          icon: <ArrowUpRightIcon strokeWidth={1} size={16} />,
                          onClick: () => navigate(ROUTES.INTERNAL_ACCESS, { state: referrer }),
                        },
                        { label: 'Remove person', icon: <Trash2Icon strokeWidth={1} size={16} />, onClick: () => onRemoveLead(l), destructive: true, divider: true, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP },
                      ]}
                    />
                  </Stack>
                ))
              )}
            </Section>

            <Section title="Stage history">
              {data.events.length === 0 ? (
                <Typography variant="body3" color="text.secondary">
                  Nothing yet.
                </Typography>
              ) : (
                <Stack spacing={3}>
                  {/* VRG-701 — the history of the deal the panel is on. */}
                  {data.events.filter((e) => !deal || e.deal_id === deal.id).map((e) => (
                    <Stack key={e.id} direction="row" spacing={3} sx={{ minWidth: 0 }}>
                      <Typography variant="body3" color="text.secondary" sx={{ width: 76, flexShrink: 0 }}>
                        {shortDate(e.occurred_at)}
                      </Typography>
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography variant="body3">{eventLine(e)}</Typography>
                        <Typography variant="body3" color="text.secondary">
                          {e.set_by === 'auto' ? 'Automatic' : 'By hand'}
                          {e.actor ? ` · ${e.actor}` : ''}
                          {e.evidence && e.kind !== 'closed' && eventLine(e) !== e.evidence ? ` · ${e.evidence}` : ''}
                          {e.subscription_id ? ` · ${e.subscription_id}` : ''}
                          {e.note ? ` · ${e.note}` : ''}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
              {deals.length <= 1 && data.closed_deals.length > 0 && (
                <Typography variant="body3" color="text.secondary">
                  Earlier deals: {data.closed_deals.map((d) => `${STAGE_LABEL[d.stage as SalesStage]} (${d.closed_reason})`).join(', ')}
                </Typography>
              )}
            </Section>
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}
